import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { useGetSchoolReportsQuery } from '../../store/api/schoolAdminApi'
import { useSchoolFilters } from '../../components/school/SchoolLayout'
import DateRangeBar from '../../components/school/DateRangeBar'
import { DemoBanner, InsightCard } from '../../components/school/SchoolUi'

type ChartMetric = 'sessions' | 'participationPct' | 'completionPct' | 'followUpPct' | 'reflectionsPct'

const SchoolReports: React.FC = () => {
  const { t } = useTranslation('school')
  const { filters } = useSchoolFilters()
  const { data, isLoading } = useGetSchoolReportsQuery(filters)
  const [chartMetric, setChartMetric] = useState<ChartMetric>('participationPct')

  const chartData = useMemo(
    () => (data?.byClass || []).map((row: any) => ({
      name: row.name?.length > 14 ? `${row.name.slice(0, 12)}…` : row.name,
      fullName: row.name,
      sessions: row.sessions || 0,
      participationPct: row.participationPct ?? 0,
      completionPct: row.completionPct ?? 0,
      followUpPct: row.followUpPct ?? 0,
      reflectionsPct: row.reflectionsPct ?? 0,
      activities: row.activities || 0,
    })),
    [data],
  )

  const metricLabel = t(`reports.chartMetrics.${chartMetric}`)
  const isPercentMetric = chartMetric !== 'sessions'

  if (isLoading) return <div className="text-gray-500 py-12">{t('common.loading')}</div>

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('reports.title')}</h1>
        <p className="mt-1 text-gray-600">{t('reports.subtitle')}</p>
      </div>

      {data?.isDemo ? <DemoBanner label={t('common.demoBanner')} /> : null}

      <DateRangeBar showClassFilter={false} />

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-lg font-semibold text-gray-900">{t('reports.usageByClassChart')}</h2>
          <select
            value={chartMetric}
            onChange={(e) => setChartMetric(e.target.value as ChartMetric)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="participationPct">{t('reports.chartMetrics.participationPct')}</option>
            <option value="completionPct">{t('reports.chartMetrics.completionPct')}</option>
            <option value="followUpPct">{t('reports.chartMetrics.followUpPct')}</option>
            <option value="reflectionsPct">{t('reports.chartMetrics.reflectionsPct')}</option>
            <option value="sessions">{t('reports.chartMetrics.sessions')}</option>
          </select>
        </div>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-500 py-12 text-center">{t('reports.emptyInsights')}</p>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  domain={isPercentMetric ? [0, 100] : ['auto', 'auto']}
                  tickFormatter={(v) => (isPercentMetric ? `${v}%` : String(v))}
                />
                <Tooltip
                  formatter={(value: number) => [
                    isPercentMetric ? `${value}%` : value,
                    metricLabel,
                  ]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                />
                <Bar dataKey={chartMetric} fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('reports.insights')}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{t('reports.insightsSubtitle')}</p>
        </div>
        {!(data?.insights || []).length ? (
          <p className="text-sm text-gray-500 bg-white border border-gray-200 rounded-xl px-5 py-8 text-center">
            {t('reports.emptyInsights')}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.insights.map((insight: any, idx: number) => (
              <InsightCard
                key={`${insight.key}-${idx}`}
                title={String(t(`reports.insightTitle.${insight.key}`, insight.params || {}))}
                body={String(t(`reports.insight.${insight.key}`, insight.params || {}))}
              />
            ))}
          </div>
        )}
      </section>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">{t('reports.byClass')}</h2>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.class')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.activeStudents')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.participation')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.sessions')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.avgInteractions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.byClass || []).map((row: any) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">
                  <Link to={`/pending/classes/${row.id}`} className="text-blue-600 hover:underline">{row.name}</Link>
                </td>
                <td className="px-4 py-3 text-sm font-medium">
                  {row.activeStudents}
                  <span className="text-gray-400 font-normal"> / {row.studentCount ?? '—'}</span>
                </td>
                <td className="px-4 py-3 text-sm">{row.participationPct != null ? `${row.participationPct}%` : '—'}</td>
                <td className="px-4 py-3 text-sm">{row.sessions}</td>
                <td className="px-4 py-3 text-sm">{row.avgInteractions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">{t('reports.byTeacher')}</h2>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.teacher')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.classes')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.activeStudents')}</th>
              <th className="px-4 py-2 text-left text-xs uppercase text-gray-500">{t('reports.columns.sessions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.byTeacher || []).map((row: any) => (
              <tr key={row.userId} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">
                  <Link to={`/pending/teachers/${row.userId}`} className="text-blue-600 hover:underline">{row.name}</Link>
                </td>
                <td className="px-4 py-3 text-sm">{row.classes}</td>
                <td className="px-4 py-3 text-sm font-medium">
                  {row.activeStudents}
                  <span className="text-gray-400 font-normal"> / {row.totalStudents ?? '—'}</span>
                </td>
                <td className="px-4 py-3 text-sm">{row.sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 text-center">
        {t('reports.adoptionScore')}: {data?.adoptionScore ?? 0}% — {t('overview.adoptionScoreHelp')}
      </p>
    </div>
  )
}

export default SchoolReports
